import { useState, useEffect } from 'react';

import useUsers from '../context/UsersContext';
import useMenus from '../context/MenuContext';
import useConfig from '../context/ConfigContext';
import useParam from '../context/ParamContext';
import { useLoader } from '../provider/LoaderProvider';
import { generateAutocompleteItems } from '../utils/helper';
import masterDataService from '../service/masterData.service';
import { ItemKind } from '../config/item';
import formPrintTemplateService from '../service/formPrintTemplate.service';

export const useService = () => {
  const { showLoader, hideLoader, loading } = useLoader();
  const { getUser, user } = useUsers();
  const { config, getConfig } = useConfig();
  const { params, getParam } = useParam();
  const { menus, getMenus, getMenuSubMenuId, getCanEdit } = useMenus();
  const [refetchTable, setRefetchTable] = useState(false);
  const [viewField, setViewField] = useState(true);
  const [selectedValues, setSelectedValues] = useState({});
  const [operatorsDisabled, setOperatorsDisabled] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [autocompleteOptions, setAutoCompleteOptions] = useState({
    quantityUnit: [],
    items: [],
    company: [],
    project: [],
    serviceTaskType: [],
    costCenter: [],
    serviceStatus: [],
    priority: [],
    user: [],
    workArea: [],
    equipmentItems: [],
  });
  const [canEdit, setCanEdit] = useState(true);
  const [autocompleteData, setAutoCompleteData] = useState({});
  const [defaultValues, setDefaultValues] = useState({});
  const [hasPrintTemplate, setHasPrintTemplate] = useState(false);

  useEffect(() => {
    getMenus();
    getConfig();
    getParam();
    if (!Object.keys(user).length) getUser();
  }, []);

  useEffect(() => {
    if (!Object.keys(menus).length) return;
    const submenuId = getMenuSubMenuId('service');
    setCanEdit(getCanEdit(submenuId));
    initDefaultValues();
  }, [menus]);

  const initDefaultValues = () => {
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .quantityUnitToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .itemsFromTableToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .companyToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .projectToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .serviceTaskTypeToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .costCenterToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .serviceStatusToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    const submenuId = getMenuSubMenuId('service');
    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .priorityToForm(submenuId)
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .usersToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .workAreaToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .serviceOperationToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        formPrintTemplateService
          .getTemplates()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    showLoader();

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          quantityUnit: resp[0],
          items: resp[1],
          company: resp[2],
          project: resp[3],
          serviceTaskType: resp[4],
          costCenter: resp[5],
          serviceStatus: resp[6],
          priority: resp[7],
          user: resp[8],
          workArea: resp[9],
          serviceOperation: resp[10],
          //11 foglalt print templatekre
        };

        setAutoCompleteData(aData);

        const equipmentItems = aData.items.filter((item) => item.kind === ItemKind.EQUIPMENT && item.active);

        const acOptions = {
          quantityUnit: generateAutocompleteItems(aData.quantityUnit, 'name', 'id'),
          items: generateAutocompleteItems(aData.items, ['itemCode', 'itemName'], 'itemCode'),
          company: generateAutocompleteItems(aData.company, 'name', 'id'),
          project: generateAutocompleteItems(aData.project, 'projectName', 'projectId'),
          serviceTaskType: generateAutocompleteItems(aData.serviceTaskType, 'name', 'id'),
          costCenter: generateAutocompleteItems(aData.costCenter, ['code', 'name'], 'code'),
          serviceStatus: generateAutocompleteItems(aData.serviceStatus, 'name', 'code'),
          priority: generateAutocompleteItems(aData.priority.priorityListItems, 'itemName', 'itemId'),
          user: generateAutocompleteItems(aData.user, 'nickName', 'userId'),
          workArea: generateAutocompleteItems(aData.workArea, ['code', 'name'], 'id'),
          serviceOperation: generateAutocompleteItems(aData.serviceOperation, ['code', 'name'], 'id'),
          equipmentItems: generateAutocompleteItems(equipmentItems, ['itemCode', 'itemName'], 'itemCode'),
        };
        setAutoCompleteOptions(acOptions);
        const defaultStatus = aData?.serviceStatus?.find((status) => status.defaultValue);
        const defaultPriority = aData?.priority?.priorityListItems.find(
          (pri) => pri.itemId === aData?.priority?.defaultValue
        );

        setDefaultValues((prev) => ({
          ...prev,
          serviceStatusId: defaultStatus ? defaultStatus.code : null,
          priorityId: defaultPriority ? defaultPriority.itemId : null,
          userId: user?.userId ? user.userId : null,
        }));

        const printTemplates = resp[11];
        const hasServicePrintTemplate = printTemplates.some((template) => template.form?.url === 'service');

        setHasPrintTemplate(hasServicePrintTemplate);
      })
      .finally(() => {
        hideLoader();
      });
  };

  return {
    refetchTable,
    setRefetchTable,
    viewField,
    setViewField,
    selectedValues,
    setSelectedValues,
    operatorsDisabled,
    setOperatorsDisabled,
    rowSelectionModel,
    setRowSelectionModel,
    autocompleteOptions,
    setAutoCompleteOptions,
    menus,
    user,
    canEdit,
    config,
    autocompleteData,
    params,
    setAutoCompleteData,
    loading,
    defaultValues,
    setDefaultValues,
    hasPrintTemplate,
  };
};
